import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { equals, reject, uniq } from 'ramda';

import { getRelocationPoints } from 'api/relocationPoints';
import { isDefined } from 'utils/ramda';

import { useRelocationPointMutations } from './useRelocationPointMutations';

type TFNData = { data?: { arrivalTo?: string; departureFrom?: string } };
type THelpers = Array<(values: TFNData) => string | undefined>;
type TWithPointsResolve = (fn: Function, helpers: THelpers) => (values: TFNData) => any;

export const useRelocationPoints = (fieldValues: string[]) => {
  const [values, setValues] = React.useState<string[]>(fieldValues.filter(isDefined));
  const { postRelocationPointMutation, deleteRelocationPointMutation } = useRelocationPointMutations();

  const points = useQuery(['relocationPoints'], getRelocationPoints).data ?? [];
  const safePoints = uniq([...values, ...points]).map((value) => ({ label: value, value }));

  const withPointsResolve: TWithPointsResolve = (fn, helpers) => (values) => {
    for (const getPoint of helpers) {
      const point = getPoint(values);
      if (point && !points.includes(point)) postRelocationPointMutation.mutate(point);
    }

    return fn(values);
  };

  const deleteRelocationPoint = (point: string) => {
    setValues(reject(equals(point)));
    deleteRelocationPointMutation.mutate(point);
  };

  const createRelocationPoint = (point: string) => {
    postRelocationPointMutation.mutate(point);
  };

  return {
    relocationPoints: safePoints,
    withPointsResolve,
    createRelocationPoint,
    deleteRelocationPoint,
  };
};
